import { memo, useEffect, useMemo, useState } from "react";
import { Button, Segment, Image, Icon, Header, Dimmer, Loader } from "semantic-ui-react";

// Commons
import { Table } from "react-lib/frameworks/Table";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";

// Serializers
import PublishedDiagRuleNodeSerializer from "@/issara-sdk/types/PublishedDiagRuleNodeSerializer_apps_PRX";
import BroadcastPostSerializer from "@/issara-sdk/types/BroadcastPostSerializer_apps_PRX";

// Types
import { FlatNode, TreeNode, PickedState, BroadcastNodeList, RunSequence, DEFAULT_BROADCAST_LIST_LIMIT, ALL_BROADCAST_STATUSES, BROADCAST_LIST_SEGMENT_NAME, COLLECTION, BroadcastDataFirebase } from "./MeFormType";

// Interface
import { ICONS, STYLES } from "./sequence/MeForm";

// Utils
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";
import { getPostById } from "./MeBroadcastUtils";

type SegmentBroadcastListProps = {
  runSequence: RunSequence;
  // data
  selectedNode: FlatNode | TreeNode | null;
  pdrList: PublishedDiagRuleNodeSerializer[];
  broadcastNodeList: BroadcastNodeList;
  // func
  onOpenPostBroadcast: (node: FlatNode | TreeNode | null) => void;
} & Pick<PickedState, "BillingCreditWallet" | "loadingStatus">;

type FirebaseMapped = BroadcastPostSerializer & Partial<{
  firebase_content: string;
  previews_picture: any[];
  previews_video: any[];
  previews_text: string;
  shorthen_text: string;
}>;

const RenderPostDetail = ({ image, text }: { image: string | null, text: string }) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        width: "100%"
      }}
    >
      {/* --- Image --- */}
      {image ? (
        <img
          src={image}
          style={{
            width: "40px",
            height: "40px",
            objectFit: "cover",
            borderRadius: "4px",
            flexShrink: 0
          }}
        />
      ) : null}

      {/* --- Text --- */}
      <div
        style={{
          flexGrow: 1,
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          // fontSize: "12px"
        }}
      >
        {text || ""}
      </div>
    </div>
  )
};

const SegmentBroadcastList = (props: SegmentBroadcastListProps) => {
  const [firebaseMappedList, setFirebaseMappedList] = useState<FirebaseMapped[]>([]);

  const { selectedNode, pdrList } = props;

  useEffect(() => {
    const load = async () => {
      const items = props.broadcastNodeList?.items || [];

      const mapped = await Promise.all(
        items.map(async (item) => {
          if (!item.firebase_id) return item;

          const fbData = await getPostById(COLLECTION, item.firebase_id) as BroadcastDataFirebase;

          return {
            ...item,
            firebase_content: fbData?.content || "",
            previews_picture: fbData?.previews_picture || [],
            previews_video: fbData?.previews_video || [],
            previews_text: fbData?.previews_text || "",
            shorthen_text: fbData?.shorthen_text || "",
          };
        })
      );

      setFirebaseMappedList(mapped);
    };

    load();
  }, [props.broadcastNodeList]);

  const handlePageChange = (activePage: number) => {
    const publishedNodeId = pdrList.find(pdr => pdr.node_id === selectedNode?.id)?.id || 0;

    props.runSequence({
      sequence: "MeForm",
      action: "GET_BROADCAST_NODE_LIST",
      broadcastNodeParams: {
        published_diag_rule_node: publishedNodeId,
        statuses: ALL_BROADCAST_STATUSES,
      },
      activePage
    });
  };

  const broadcastNodeList = useMemo(() => {
    return firebaseMappedList.map((item) => {
      const wrapper = document.createElement("div");
      wrapper.innerHTML = item.firebase_content || "";

      const firstImg = wrapper.querySelector("img")?.getAttribute("src")
        || item.previews_picture?.[0]
        || null;

      wrapper.querySelectorAll("img, figure, .se-image-container").forEach((el) => el.remove());
      
      const text = item?.previews_text || "";

      return {
        ...item,
        created_at: formatISOtoBEDateTime(item.created_at).dateTimeShortBracket,
        broadcasted_at: formatISOtoBEDateTime(item.broadcasted_at).dateTimeShortBracket,
        content: (
          <RenderPostDetail image={firstImg} text={text} />
        )
      }
    })
  }, [firebaseMappedList]);

  return (
    <>
      <Segment
        style={{
          border: "solid 1px var(--sidebar-light-bg)",
          backgroundColor: "var(--sidebar-light-bg)",
          borderRadius: 0,
          padding: "10px",
          height: "100%",
          // minHeight: "400px"
        }}
      >
        <Dimmer active={props.loadingStatus?.[BROADCAST_LIST_SEGMENT_NAME]} inverted>
          <Loader inverted>Loading</Loader>
        </Dimmer>

        <Button
          size="small"
          style={{ ...STYLES.primaryThemeBtn, display: "flex", gap: "8px", alignItems: "center" }}
          onClick={() => props.onOpenPostBroadcast?.(selectedNode)}
        >
          <Image height="18px" width="18px" src={ICONS.broadcast} />
          <label style={{ cursor: "pointer" }}>Create Broadcast</label>
        </Button>

        <Segment
          style={{
            border: "solid 1px var(--primary-theme-color)",
            backgroundColor: "var(--sidebar-light-bg)",
          }}
        >
          <Header
            style={{
              display: "flex",
              color: "var(--primary-theme-color)",
              alignItems: "center",
              gap: "8px",
            }}
          >
            Broadcast History
            <Image style={{ width: "20px", height: "20px" }} src={ICONS.bullhorn} />
          </Header>

          <Table
            style={{
              maxHeight: "210px",
              overflowY: "auto",
            }}
            data={broadcastNodeList}
            headers="Broadcast date, ID, Post/Detail, Status"
            keys="broadcasted_at, broadcast_id, content, status_custom_label"
            minRows={5}
            showPagination={false}
            widths="^120, ^100, ^180, ^70"
          />

          <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "10px" }}>
            <PaginationShorthand
              activePage={props.broadcastNodeList?.activePage || 1}
              limit={DEFAULT_BROADCAST_LIST_LIMIT}
              total={props.broadcastNodeList?.total}
              onPageChange={handlePageChange}
            />
          </div>
        </Segment>
      </Segment>
    </>
  );
};

export default SegmentBroadcastList;
